/*
 * RIDECLOUD NAVBAR
 * Style: Dark purple bg, orange logo, white links, orange hamburger
 * Colours: #2d1b4e bg, white text, #e6531e hover accent & hamburger
 * No CTA button — matches original minimal nav
 */

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "wouter";

const LOGO_ORANGE =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663326861339/FdaTdcVYATQM4PxPcH7X9e/ridecloud-master-logo-primary-orange_cb013153.png";

const navLinks = [
  { label: "Vehicles", href: "/vehicles" },
  { label: "Why Subscribe?", href: "#why-subscribe" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Contact Us", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      // Page navigation — let the browser handle it via href
      return;
    }
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Not on homepage — navigate to home with the hash so the section is reached
      navigate("/" + href);
    }
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: "#2d1b4e",
          backdropFilter: "blur(8px)",
          boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.25)" : "0 1px 0 rgba(0,0,0,0.15)",
        }}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center gap-2 flex-shrink-0"
              aria-label="RideCloud home"
            >
              <img src={LOGO_ORANGE} alt="RideCloud" className="h-10 w-auto" />
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-7">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(link.href, e)}
                  className="text-sm font-medium transition-colors duration-150"
                  style={{ color: "rgba(255,255,255,0.85)", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#e6531e")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.85)")}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg"
              style={{ color: "#e6531e" }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="md:hidden border-t"
            style={{ backgroundColor: "#2d1b4e", borderColor: "rgba(255,255,255,0.10)" }}
          >
            <div className="container py-3 flex flex-col gap-0.5">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(link.href, e)}
                  className="px-4 py-3 rounded-lg text-sm font-medium block transition-colors"
                  style={{ color: "rgba(255,255,255,0.85)", textDecoration: "none" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(230,83,30,0.12)"; e.currentTarget.style.color = "#e6531e"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>
      {/* Spacer for fixed navbar */}
      <div className="h-16" />
    </>
  );
}
